import {
    Badge,
    Button,
    ButtonSizes,
    ButtonStyles,
    Icon,
    IconButton,
    IconButtonStyles,
    Icons,
    Label,
    List,
    ListItem,
    MenuButton,
    TextInput,
    Tooltip
} from '@silverfort/sf-components';
import { useServiceAccountContext } from '../../../contexts/ServiceAccountContext';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CustomOwnerData, CustomOwnerType, OwnerFullData, OwnersAddedBy } from '../../../ServiceAccountsTypes';
import OwnerSelect from './SaOwnerSelect';
import useHasPermissions from '../../../../../../hooks/useHasPermissions';
import { Pages, Roles } from '../../../../../../context/contexts/UserContext/permissionsTypes';

const MAX_OWNERS = 5;

const ADDED_BY_TO_CONFIG = {
    [OwnersAddedBy.API]: { icon: Icons.Auto, tooltip: 'Added via API' },
    [OwnersAddedBy.UI]: { icon: Icons.UserEdit, tooltip: 'Assigned manually' },
    [OwnersAddedBy.AD_SYNCED]: { icon: Icons.WindowCog, tooltip: 'Synced from AD' }
};

const isSameCustomOwner = (customOwner1: CustomOwnerData, customOwner2: CustomOwnerData) => {
    return customOwner1.owner_type === customOwner2.owner_type && customOwner1.owner_value === customOwner2.owner_value;
};

interface OwnerRowProps {
    ownerTitle: string;
    ownerIcon: keyof typeof Icons;
    ownerAddedBy: OwnersAddedBy;
    index: number;
    isEditor: boolean;
    onEdit: () => void;
    onRemove: () => void;
}

const OwnerRow: FC<OwnerRowProps> = ({ ownerTitle, ownerIcon, ownerAddedBy, index, isEditor, onEdit, onRemove }) => {
    return (
        <div className="flex gap-4 items-center">
            <div className="font-sf text-sf text-sf-gray-500 flex gap-1">
                <div>Owner {index + 1}</div>
                <div className="flex items-center">
                    <Tooltip title={ADDED_BY_TO_CONFIG[ownerAddedBy].tooltip}>
                        <Icon icon={ADDED_BY_TO_CONFIG[ownerAddedBy].icon} />
                    </Tooltip>
                </div>
            </div>
            <div>
                <Badge
                    icon={ownerIcon}
                    text={ownerTitle}
                    iconButtonProps={isEditor ? { icon: Icons.Cancel, onClick: onRemove } : undefined}
                    className="font-medium"
                    maxWidth="180px"
                />
            </div>

            {isEditor && (
                <div>
                    <Button
                        iconRight={Icons.Edit}
                        onClick={onEdit}
                        buttonStyle={ButtonStyles.Primary}
                        buttonSize={ButtonSizes.Small}
                    />
                </div>
            )}
        </div>
    );
};

interface ADOwnerRowProps {
    owner: OwnerFullData;
    index: number;
    isEditor: boolean;
    onEdit: () => void;
    onRemove: () => void;
}

const ADOwnerRow: FC<ADOwnerRowProps> = ({ owner, index, isEditor, onEdit, onRemove }) => {
    return (
        <OwnerRow
            ownerTitle={`${owner.name} | ${owner.domain}${owner.is_disabled ? ' (disabled)' : ''}`}
            ownerIcon={'ActiveDirectory'}
            ownerAddedBy={owner.added_by}
            index={index}
            isEditor={isEditor}
            onEdit={onEdit}
            onRemove={onRemove}
        />
    );
};

interface CustomOwnerRowProps {
    owner: CustomOwnerData;
    index: number;
    isEditor: boolean;
    onEdit: () => void;
    isOnEdit: boolean;
    onRemove: () => void;
}

const CustomOwnerRow: FC<CustomOwnerRowProps> = ({ owner, index, isEditor, onEdit, isOnEdit, onRemove }) => {
    const ownerIcon = owner.owner_type === CustomOwnerType.EMAIL ? 'Atag' : 'Tune';
    if (isOnEdit) {
        return <></>;
    }
    return (
        <OwnerRow
            ownerTitle={owner.owner_value}
            ownerIcon={ownerIcon}
            ownerAddedBy={owner.added_by}
            index={index}
            isEditor={isEditor}
            onEdit={onEdit}
            onRemove={onRemove}
        />
    );
};

interface UpdateCustomOwnerProps {
    ownerData: CustomOwnerData;
    isEdit: boolean;
    onApply: (editedOwner: CustomOwnerData) => void;
    onCancel?: () => void;
    ownerIndex: number;
}

const UpdateCustomOwner: FC<UpdateCustomOwnerProps> = ({
    ownerData,
    isEdit,
    onApply,
    onCancel,
    ownerIndex: numberOfOwners
}) => {
    const [newCustomOwner, setNewCustomOwner] = useState<CustomOwnerData>({ ...ownerData });
    const [isValid, setIsValid] = useState(!!ownerData.owner_value);

    useEffect(() => {
        setNewCustomOwner({ ...ownerData });
    }, [ownerData]);

    useEffect(() => {
        if (newCustomOwner.owner_type === CustomOwnerType.EMAIL) {
            const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomOwner.owner_value);
            setIsValid(isValidEmail);
        } else {
            setIsValid(newCustomOwner.owner_value.length > 0);
        }
    }, [newCustomOwner]);

    const handleInputChange = (value: string) => {
        setNewCustomOwner({ ...newCustomOwner, owner_value: value });
    };

    return (
        <div className="flex gap-4 items-center">
            <div className="font-sf text-sf text-sf-gray-500">Owner {numberOfOwners + 1}</div>
            <TextInput onChange={handleInputChange} value={newCustomOwner.owner_value} className="h-8" />
            <IconButton icon={Icons.Cancel} onClick={onCancel} buttonStyle={IconButtonStyles.Secondary} />
            <Button
                buttonStyle={ButtonStyles.Primary}
                text={isEdit ? 'Update' : 'Apply'}
                onClick={() => onApply(newCustomOwner)}
                disabled={!isValid}
            />
        </div>
    );
};

export const SaOwners: FC = () => {
    const isEditor = useHasPermissions({ permission: Pages.SERVICE_ACCOUNTS, role: Roles.VIEWER });

    const { updatedServiceAccount, selectedOwnersData, updateServiceAccountData } = useServiceAccountContext();
    const [adOwners, setAdOwners] = useState<OwnerFullData[]>([]);
    const [emailOwners, setEmailOwners] = useState<CustomOwnerData[]>([]);
    const [customOwners, setCustomOwners] = useState<CustomOwnerData[]>([]);
    const [customOwnerToUpdate, setCustomOwnerToUpdate] = useState<CustomOwnerData | undefined>();
    const [customOwnerBeforeUpdate, setCustomOwnerBeforeUpdate] = useState<CustomOwnerData | undefined>();

    const customOwnerEditIndex = useMemo(() => {
        if (!customOwnerBeforeUpdate) {
            return adOwners.length + emailOwners.length + customOwners.length;
        }
        if (customOwnerBeforeUpdate.owner_type === CustomOwnerType.EMAIL) {
            return (
                adOwners.length + emailOwners.findIndex((owner) => isSameCustomOwner(owner, customOwnerBeforeUpdate))
            );
        }
        if (customOwnerBeforeUpdate.owner_type === CustomOwnerType.CUSTOM_TEXT) {
            return (
                adOwners.length +
                emailOwners.length +
                customOwners.findIndex((owner) => isSameCustomOwner(owner, customOwnerBeforeUpdate))
            );
        }
    }, [adOwners.length, emailOwners, customOwners, customOwnerBeforeUpdate]);

    const buttonRef = useRef<HTMLDivElement>(null);
    const [openAdOwnerSelect, setOpenAdOwnerSelect] = useState(false);

    useEffect(() => {
        if (selectedOwnersData) {
            setAdOwners(selectedOwnersData);
        }
    }, [selectedOwnersData]);

    useEffect(() => {
        if (updatedServiceAccount?.customOwners) {
            const emailOwners = updatedServiceAccount.customOwners.filter(
                (owner) => owner.owner_type === CustomOwnerType.EMAIL
            );
            const customOwners = updatedServiceAccount.customOwners.filter(
                (owner) => owner.owner_type === CustomOwnerType.CUSTOM_TEXT
            );

            setEmailOwners(emailOwners);
            setCustomOwners(customOwners);
        }
    }, [updatedServiceAccount?.customOwners]);

    const addCustomOwner = (owner) => {
        setCustomOwnerToUpdate(owner);
        setCustomOwnerBeforeUpdate(undefined);
    };

    const selectOwnerToUpdate = (owner: CustomOwnerData) => {
        setCustomOwnerToUpdate(owner);
        setCustomOwnerBeforeUpdate(owner);
    };

    const unselectOwnerToUpdate = () => {
        setCustomOwnerToUpdate(undefined);
        setCustomOwnerBeforeUpdate(undefined);
    };

    const handleUpdateCustomOwner = (owner: CustomOwnerData) => {
        const newCustomOwners = [...(updatedServiceAccount?.customOwners || [])];
        const editIndex = newCustomOwners.findIndex(
            (o) =>
                customOwnerToUpdate &&
                o.owner_type === customOwnerToUpdate.owner_type &&
                o.owner_value === customOwnerToUpdate?.owner_value
        );

        const existIndex = newCustomOwners.findIndex(
            (o) => o.owner_value === owner.owner_value && o.owner_type === owner.owner_type
        );

        if (editIndex === -1) {
            if (existIndex === -1) {
                newCustomOwners.push(owner);
            }
        } else {
            if (existIndex === -1) {
                newCustomOwners[editIndex] = { ...owner };
            } else if (existIndex !== editIndex) {
                // Incase owner was changed to existing one.
                newCustomOwners.splice(editIndex, 1);
            }
        }

        updateServiceAccountData('customOwners', newCustomOwners);
        unselectOwnerToUpdate();
    };

    const handleRemoveAdOwner = (owner: OwnerFullData) => {
        const newAdOwners = adOwners.filter((o) => o.guid !== owner.guid);
        updateServiceAccountData(
            'account_owners',
            newAdOwners.map((o) => ({ owner_guid: o.guid, added_by: o.added_by }))
        );
    };

    const handleRemoveCustomOwner = (owner: CustomOwnerData) => {
        const newCustomOwners =
            updatedServiceAccount?.customOwners.filter(
                (o) => !(o.owner_type === owner.owner_type && o.owner_value === owner.owner_value)
            ) || [];
        updateServiceAccountData('customOwners', newCustomOwners);
    };

    const totalOwnersLength = adOwners.length + emailOwners.length + customOwners.length;

    return (
        <div className="flex gap-6">
            <div className={`flex ${customOwnerToUpdate || !totalOwnersLength ? 'h-8' : 'h-[24px]'}`}>
                <Label text="Owner" className="w-16 self-center text-sf-gray-800" />
            </div>
            <div className="grid gap-4">
                {customOwnerToUpdate && (
                    <div>
                        <UpdateCustomOwner
                            ownerData={customOwnerToUpdate}
                            isEdit={!!customOwnerBeforeUpdate}
                            onApply={handleUpdateCustomOwner}
                            onCancel={unselectOwnerToUpdate}
                            ownerIndex={customOwnerEditIndex || 0}
                        />
                    </div>
                )}
                {!!totalOwnersLength && (
                    <div className="grid gap-2">
                        {adOwners.map((owner, index) => (
                            <ADOwnerRow
                                key={index}
                                owner={owner}
                                isEditor={isEditor}
                                index={index}
                                onEdit={() => setOpenAdOwnerSelect(true)}
                                onRemove={() => handleRemoveAdOwner(owner)}
                            />
                        ))}
                        {emailOwners.map((owner, index) => (
                            <CustomOwnerRow
                                key={index}
                                owner={owner}
                                isEditor={isEditor}
                                index={index + adOwners.length}
                                onEdit={() => selectOwnerToUpdate(owner)}
                                isOnEdit={
                                    !!customOwnerBeforeUpdate && isSameCustomOwner(customOwnerBeforeUpdate, owner)
                                }
                                onRemove={() => handleRemoveCustomOwner(owner)}
                            />
                        ))}
                        {customOwners.map((owner, index) => (
                            <CustomOwnerRow
                                key={index}
                                owner={owner}
                                isEditor={isEditor}
                                index={index + adOwners.length + emailOwners.length}
                                onEdit={() => selectOwnerToUpdate(owner)}
                                isOnEdit={
                                    !!customOwnerBeforeUpdate && isSameCustomOwner(customOwnerBeforeUpdate, owner)
                                }
                                onRemove={() => handleRemoveCustomOwner(owner)}
                            />
                        ))}
                    </div>
                )}
                {isEditor && (
                    <div ref={buttonRef}>
                        <OwnerSelect
                            menuOpen={openAdOwnerSelect}
                            anchorEl={buttonRef}
                            onClose={() => setOpenAdOwnerSelect(false)}
                            maxOwners={MAX_OWNERS - emailOwners.length - customOwners.length}
                        />
                        <MenuButton
                            text="Add"
                            icon={Icons.Add}
                            openByDefault={false}
                            iconLeft={Icons.Add}
                            buttonStyle={ButtonStyles.Primary}
                            buttonSize={ButtonSizes.Medium}
                            disabled={totalOwnersLength >= MAX_OWNERS}
                            buttonTooltipProps={
                                totalOwnersLength >= MAX_OWNERS
                                    ? { title: 'Cannot assign more than five owners per account' }
                                    : undefined
                            }
                            content={
                                <List className="p-[0]">
                                    <ListItem
                                        title="Add from Active Directory"
                                        onClick={() => {
                                            setOpenAdOwnerSelect((openAdOwnerSelect) => !openAdOwnerSelect);
                                        }}
                                    />
                                    <ListItem
                                        title="Add by Email"
                                        onClick={() =>
                                            addCustomOwner({
                                                owner_type: CustomOwnerType.EMAIL,
                                                owner_value: '',
                                                added_by: OwnersAddedBy.UI
                                            })
                                        }
                                    />
                                    <ListItem
                                        title="Add by Custom ID"
                                        onClick={() =>
                                            addCustomOwner({
                                                owner_type: CustomOwnerType.CUSTOM_TEXT,
                                                owner_value: '',
                                                added_by: OwnersAddedBy.UI
                                            })
                                        }
                                    />
                                </List>
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
